import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface Props {
  topicKeywords: string[]
  value: string[]
  onChange: (next: string[]) => void
}

export function NetworkKeywordChips({ topicKeywords, value, onChange }: Props) {
  const { t } = useTranslation('network')

  if (topicKeywords.length === 0) return null

  function toggle(kw: string) {
    if (value.includes(kw)) {
      onChange(value.filter((v) => v !== kw))
    } else {
      onChange([...value, kw])
    }
  }

  function clearAll() {
    onChange([])
  }

  return (
    <div
      role="group"
      aria-label={t('keywordChips.label')}
      className="flex flex-wrap items-center gap-2"
    >
      {value.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          onKeyDown={(e) => {
            if (e.key === 'Escape') clearAll()
          }}
          className={cn(
            'flex min-h-[36px] shrink-0 items-center rounded-full px-3 py-1.5 text-xs font-semibold',
            'bg-primary/10 text-primary ring-1 ring-primary/30',
            'transition-all active:scale-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none',
            'hover:bg-primary/20',
          )}
        >
          {t('keywordChips.clear')}
        </button>
      )}

      {topicKeywords.map((kw) => {
        const selected = value.includes(kw)
        return (
          <button
            key={kw}
            type="button"
            onClick={() => toggle(kw)}
            onKeyDown={(e) => {
              if (e.key === ' ') {
                e.preventDefault()
                toggle(kw)
              }
              if (e.key === 'Escape') clearAll()
            }}
            aria-pressed={selected}
            className={cn(
              'flex min-h-[36px] shrink-0 items-center rounded-full px-3 py-1.5 text-xs font-medium',
              'transition-all active:scale-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none',
              selected
                ? 'bg-card text-foreground ring-1 ring-primary/40 shadow-sm'
                : 'bg-secondary/30 text-muted-foreground ring-1 ring-border hover:text-foreground hover:bg-secondary/60',
            )}
          >
            {kw}
          </button>
        )
      })}
    </div>
  )
}
